'use client';
import { useEffect, useState } from 'react';

// useResource — single shared primitive for client-side GET fetches.
//
// Replaces the ad-hoc `useEffect + fetch + useState` pattern that was
// scattered across components. Fixes three long-standing glitches:
//
//   1. Duplicate fetches. Two components asking for /api/data-surfaces
//      at the same tick used to fire two HTTP calls (each useEffect was
//      independent). Now: module-level cache + inflight Promise dedup,
//      so concurrent requests for the same URL coalesce into one.
//
//   2. Repaint flicker. Each component used to flip from null → data
//      independently, causing visual jank. Now: once the cache has the
//      data, late-mounting components paint with it on the first render
//      via the lazy state initializer (no loading flash).
//
//   3. Long-open tabs showing stale data. Cache used to live forever for
//      the page session, so a tab open for 13 days showed 13-day-old data.
//      Now: 5-minute TTL + tab-focus revalidation. If you tab back to a
//      page after >2 min of being hidden, the cache is cleared and the
//      hook re-renders with fresh data.

const DEFAULT_TTL_MS = 5 * 60 * 1000;          // 5 minutes
const VISIBILITY_REVALIDATE_MS = 2 * 60 * 1000; // 2 min hidden → drop on focus

interface CacheEntry<T> {
  data: T;
  ts: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();
const subscribers = new Set<() => void>();

function notifySubscribers(): void {
  for (const cb of subscribers) cb();
}

function getFresh<T>(url: string, ttlMs: number): T | undefined {
  const entry = cache.get(url) as CacheEntry<T> | undefined;
  if (entry == null) return undefined;
  return Date.now() - entry.ts < ttlMs ? entry.data : undefined;
}

export interface ResourceState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * The cache/dedup core, isolated from React so it can be tested as a
 * plain async function. Returns a cached value when present and fresh
 * within `ttlMs`, joins an inflight request when one is pending,
 * otherwise fires a new fetch. Throws on non-2xx response or network
 * failure (callers handle).
 *
 * Successful responses populate the module-level cache. Failures are
 * NOT cached — a transient error will be retried on next request.
 */
export async function fetchWithCache<T>(url: string, ttlMs: number = DEFAULT_TTL_MS): Promise<T> {
  const fresh = getFresh<T>(url, ttlMs);
  if (fresh !== undefined) return fresh;

  let promise = inflight.get(url) as Promise<T> | undefined;
  if (!promise) {
    promise = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<T>;
      })
      .then((d) => {
        cache.set(url, { data: d, ts: Date.now() });
        return d;
      })
      .finally(() => {
        inflight.delete(url);
      });
    inflight.set(url, promise as Promise<unknown>);
  }
  return promise;
}

/** Drop a single URL from the cache so the next fetch goes to network. */
export function invalidateResource(url: string): void {
  cache.delete(url);
  inflight.delete(url);
}

// Tab-focus revalidation. When the tab is hidden for >VISIBILITY_REVALIDATE_MS
// and becomes visible again, drop the entire cache and notify every active
// useResource hook so they re-fetch. Browsers throttle background tabs and
// can leave them stale for hours; this keeps the UI honest without forcing
// a hard reload.
let lastHiddenAt = 0;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      lastHiddenAt = Date.now();
    } else if (document.visibilityState === 'visible' && lastHiddenAt > 0) {
      const hiddenMs = Date.now() - lastHiddenAt;
      lastHiddenAt = 0;
      if (hiddenMs >= VISIBILITY_REVALIDATE_MS) {
        cache.clear();
        inflight.clear();
        notifySubscribers();
      }
    }
  });
}

/**
 * Fetch a JSON resource with module-level caching, request dedup, TTL
 * staleness, and tab-focus revalidation.
 *
 * @param url   Endpoint to GET. Pass `null` to disable (e.g. when params
 *              aren't ready yet) — the hook returns idle without firing.
 * @param ttlMs Cache TTL in ms (default 5 min). After this, the next read
 *              re-fetches.
 *
 * @example
 *   const { data, loading, error } = useResource<MyShape>('/api/foo');
 *   if (loading) return <Skeleton />;
 *   if (error) return <ErrorBox msg={error} />;
 *   if (!data) return null;
 */
export function useResource<T>(url: string | null, ttlMs: number = DEFAULT_TTL_MS): ResourceState<T> {
  // tick is bumped by refetch() and by global tab-focus revalidation, both
  // of which need to force the effect to re-run.
  const [tick, setTick] = useState(0);

  // Lazy initializer: if the URL is already cached AND fresh, paint with
  // it on the first render — no loading flash.
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string | null }>(() => {
    if (url) {
      const fresh = getFresh<T>(url, ttlMs);
      if (fresh !== undefined) return { data: fresh, loading: false, error: null };
    }
    return { data: null, loading: url != null, error: null };
  });

  // Subscribe to global cache-invalidation events (tab-focus revalidation).
  useEffect(() => {
    const cb = () => setTick((t) => t + 1);
    subscribers.add(cb);
    return () => { subscribers.delete(cb); };
  }, []);

  // setState-in-effect is unavoidable here: this is a data-fetching primitive
  // whose entire purpose is to bridge async resolution into React state.
  // The lint rule (react-hooks/set-state-in-effect) targets *derived* state
  // that should be computed at render time — it doesn't have a clean escape
  // hatch for "I'm the data bridge."
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!url) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    // Cache hit AND fresh — sync state and bail. Covers (a) the URL changed
    // and the new URL is cached, (b) we just bumped tick after a tab-focus
    // revalidation and the cache was already repopulated by another caller.
    const fresh = getFresh<T>(url, ttlMs);
    if (fresh !== undefined) {
      setState({ data: fresh, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    fetchWithCache<T>(url, ttlMs)
      .then((d) => {
        if (!cancelled) setState({ data: d, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setState({ data: null, loading: false, error: msg });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url, tick, ttlMs]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return {
    ...state,
    refetch: () => {
      if (url) invalidateResource(url);
      setTick((t) => t + 1);
    },
  };
}

// ─── Test-only helpers ───────────────────────────────────────────────
// Exposed so tests can reset state between cases. Not intended for
// production code paths — use refetch() to invalidate a single URL.

export const __resourceCacheForTests = {
  clear() {
    cache.clear();
    inflight.clear();
    lastHiddenAt = 0;
  },
  size() {
    return cache.size;
  },
  has(url: string) {
    return cache.has(url);
  },
  /** Set the timestamp of an existing cache entry. Used to simulate aged
   *  entries without waiting through real time. */
  ageEntry(url: string, ageMs: number): void {
    const entry = cache.get(url);
    if (entry) cache.set(url, { ...entry, ts: Date.now() - ageMs });
  },
  /** Trigger the tab-focus revalidation pathway. Tests can't fire real
   *  visibilitychange events from JSDOM reliably; this exposes the same
   *  side effect (clear cache + notify subscribers). */
  triggerRevalidation(): void {
    cache.clear();
    inflight.clear();
    notifySubscribers();
  },
  subscriberCount(): number {
    return subscribers.size;
  },
};
